import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Typography,
  Input,
  Button,
  Switch,
  message,
  Modal,
  Tooltip,
  Skeleton,
  Divider,
  Collapse,
  Badge,
  Space,
  Empty,
} from "antd";
import UpgradeDialog from "../../../components/common/UpgradeDialog";
import {
  DeleteOutlined,
  CheckCircleOutlined,
  LockOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  getEmailConfigApi,
  addEmailRecipientApi,
  updateEmailRecipientCtaStatusApi,
  deleteEmailRecipientApi,
  updateEmailRecipientStatusApi,
  getEmailServiceStatusApi,
  updateEmailServiceStatusApi,
} from "../../../services/email.service";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const EmailSettings = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [activeKeys, setActiveKeys] = useState([]); // Collapsed by default
  const [emailServiceStatus, setEmailServiceStatus] = useState(false);
  const [serviceLoading, setServiceLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { currentPlan } = useAppSelector((state) => state.currentPlan);
  const isTrialPlan = currentPlan === 'TRIAL';

  useEffect(() => {
    setLoading(true);
    setServiceLoading(true);

    // Fetch Email Service Status
    dispatch(
      getEmailServiceStatusApi({
        successCallback: (response) => {
          setEmailServiceStatus(response?.email_service_status || false);
        },
        failureCallback: () => {
          message.error("Failed to fetch email service status.");
        },
        finalCallback: () => {
          setServiceLoading(false);
        },
      })
    );

    // Fetch Email Config
    dispatch(
      getEmailConfigApi({
        successCallback: (response) => {
          // Normalize id to _id for consistency
          const defaultCtas = response.cta_types.map((cta) => ({
            name: cta.name,
            enable: cta.enable,
            _id: cta.id,
          }));
          const normalizedResponse = {
            ...response,
            emails: response.emails.map((email) => {
              if (typeof email === 'string') {
                return {
                  email,
                  name: email.split('@')[0],
                  _id: email, // Use email as _id since no id provided
                  enable: response.enabled,
                  cta_types: defaultCtas,
                };
              } else {
                const cta_types = email.cta_types.length > 0
                  ? email.cta_types.map((cta) => ({
                    ...cta,
                    _id: cta.id || cta._id,
                  }))
                  : defaultCtas;
                return {
                  ...email,
                  _id: email.id || email._id,
                  enable: email.enable !== undefined ? email.enable : true,
                  cta_types,
                };
              }
            }),
          };
          setEmailConfig(normalizedResponse);
        },
        failureCallback: (errors) => {
          message.error("Failed to fetch email configuration.");
        },
        finalCallback: () => {
          setLoading(false);
        },
      })
    );
  }, [dispatch]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleAddEmail = () => {
    if (!emailRegex.test(newEmail)) {
      message.error("Enter a valid email address.");
      return;
    }

    const data = {
      email: newEmail,
      name: newName || newEmail.split("@")[0],
    };

    dispatch(
      addEmailRecipientApi({
        params: data,
        successCallback: (response) => {
          setEmailConfig((prev) => ({
            ...prev,
            emails: [
              ...prev.emails,
              {
                ...response.data,
                _id: response.data.id || response.data._id,
                enable:
                  response.data.enable !== undefined
                    ? response.data.enable
                    : true,
                cta_types: response.data.cta_types.map((cta) => ({
                  ...cta,
                  _id: cta.id || cta._id,
                })),
              },
            ],
          }));
          setNewEmail("");
          setNewName("");
          setIsAddModalVisible(false);
          message.success(response.message);
        },
        failureCallback: (errors) => {
          message.error("Failed to add email recipient.");
        },
        finalCallback: () => { },
      })
    );
  };

  const handleMasterSwitchChange = (checked) => {
    dispatch(
      updateEmailServiceStatusApi({
        params: { status: checked },
        successCallback: () => {
          setEmailServiceStatus(checked);
          message.success(`Email service ${checked ? "enabled" : "disabled"} successfully.`);
        },
        failureCallback: () => {
          message.error("Failed to update email service status.");
        },
        finalCallback: () => { },
      })
    );
  };

  const handleAddRecipientClick = () => {
    if (isTrialPlan) {
      setIsUpgradeModalVisible(true);
    } else {
      setIsAddModalVisible(true);
    }
  };

  const handleToggleEmailStatus = (emailId) => {
    const email = emailConfig.emails.find((e) => e._id === emailId);
    const newStatus = !email.enable;

    dispatch(
      updateEmailRecipientStatusApi({
        params: {
          id: emailId,
          status: newStatus ? "enabled" : "disabled",
        },
        successCallback: (response) => {
          setEmailConfig((prev) => ({
            ...prev,
            emails: prev.emails.map((e) =>
              e._id === emailId ? { ...e, enable: newStatus } : e
            ),
          }));
          message.success(
            `Email notifications ${newStatus ? "enabled" : "disabled"} for ${email.email
            }.`
          );
        },
        failureCallback: (errors) => {
          message.error("Failed to update email status.");
        },
        finalCallback: () => { },
      })
    );
  };

  const handleToggleCta = (emailId, ctaName) => {
    const email = emailConfig.emails.find((e) => e._id === emailId);
    const currentCta = email.cta_types.find((cta) => cta.name === ctaName);
    if (!currentCta) return;
    const newCtaEnable = !currentCta.enable;

    dispatch(
      updateEmailRecipientCtaStatusApi({
        params: {
          id: emailId,
          name: ctaName,
          enable: newCtaEnable,
        },
        successCallback: (response) => {
          setEmailConfig((prev) => ({
            ...prev,
            emails: prev.emails.map((e) =>
              e._id === emailId
                ? {
                  ...e,
                  cta_types: e.cta_types.map((cta) =>
                    cta.name === ctaName
                      ? { ...cta, enable: newCtaEnable }
                      : cta
                  ),
                }
                : e
            ),
          }));
          message.success(
            `${ctaName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} notifications updated for ${email.email}.`
          );
        },
        failureCallback: (errors) => {
          message.error("Failed to update CTA settings.");
        },
        finalCallback: () => { },
      })
    );
  };

  const handleExpandRecipient = (recipientId) => {
    setActiveKeys((prev) => {
      const keys = Array.isArray(prev) ? prev : prev ? [prev] : [];
      if (keys.includes(recipientId)) return keys;
      return [...keys, recipientId];
    });
  };

  const showDeleteModal = (id) => {
    setIsModalVisible(true);
    setEmailToDelete(id);
  };

  const handleDelete = () => {
    dispatch(
      deleteEmailRecipientApi({
        params: { id: emailToDelete },
        successCallback: (response) => {
          setEmailConfig((prev) => ({
            ...prev,
            emails: prev.emails.filter((email) => email._id !== emailToDelete),
          }));
          setIsModalVisible(false);
          setEmailToDelete(null);
          message.success(response.message);
        },
        failureCallback: (errors) => { },
        finalCallback: () => { },
      })
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
    setEmailToDelete(null);
  };

  const selectedEmailObj = emailConfig?.emails.find(
    (e) => e._id === emailToDelete
  );

  // Helper function to get notification counts for an email
  const getNotificationCounts = (cta_types) => {
    const enabled = cta_types.filter((cta) => cta.enable).length;
    const total = cta_types.length;
    return { enabled, disabled: total - enabled, total };
  };

  // Helper to format CTA name
  const formatCtaName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card
      style={{
        width: "100%",
        borderRadius: 10,
        boxShadow: "0 1px 6px rgba(0, 0, 0, 0.06)",
      }}
      bodyStyle={{
        padding: isMobile ? 16 : 24,
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 16,
          flexDirection: isMobile ? "column" : "row",
          padding: isMobile ? "12px 12px 16px" : "16px 16px 20px",
          background: "#f7f9fc",
          borderRadius: 12,
          border: "1px solid #eef2f7",
          marginBottom: 20,
        }}
      >
        <div>
          <Title level={4} style={{ marginBottom: 4, fontWeight: 600 }}>
            Email Settings
          </Title>
          <Text type="secondary">
            Manage recipients and notification rules for email alerts.
          </Text>
        </div>
        {!isTrialPlan && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 12px",
              background: "#ffffff",
              border: "1px solid #e6eef6",
              borderRadius: 10,
            }}
          >
            <Text strong>Enable Email Service</Text>
            <Switch
              checked={emailServiceStatus}
              onChange={handleMasterSwitchChange}
              loading={serviceLoading}
            />
          </div>
        )}
      </div>

      <Divider style={{ margin: "8px 0 20px 0", width: "100%" }} />

      {/* Add Email */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 12 : 16,
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 4 }}>
            Recipients
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Manage notification preferences for each recipient.
          </Text>
        </div>
        <Tooltip
          title={!isTrialPlan && !emailServiceStatus ? "Email service is disabled. Enable to add recipients." : "" }
        >
          <span>
            <Button
              type="primary"
              icon={isTrialPlan ? <LockOutlined /> : <PlusOutlined />}
              onClick={handleAddRecipientClick}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                width: isMobile ? "100%" : "auto",
              }}
              disabled={!emailServiceStatus && !isTrialPlan}
            >
              Add Recipient
            </Button>
          </span>
        </Tooltip>
      </div>



      {loading || !emailConfig ? (
        <div>
          {[...Array(2)].map((_, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <Skeleton.Input active size="small" style={{ width: 200 }} />
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <Skeleton.Button active size="small" />
                  <Skeleton.Input active size="small" style={{ width: 140 }} />
                  <Skeleton.Button active size="small" shape="circle" />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 40,
                  flexWrap: "wrap",
                  marginTop: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Skeleton.Button active size="small" />
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: 140, marginLeft: 8 }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Skeleton.Button active size="small" />
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: 140, marginLeft: 8 }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : emailConfig.emails.length === 0 ? (
        <Empty
          description={
            <Space direction="vertical" size="small">
              <Typography.Text type="secondary">
                Recipients receive email notifications for conversations and CTA.
              </Typography.Text>
              <Typography.Text type="secondary">
                Click <strong>"Add Recipient"</strong> to get started.
              </Typography.Text>
              {/* {isTrialPlan && (
                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                  Upgrade to Pro to add recipients.
                </Typography.Text>
              )} */}
            </Space>
          }
        />
      ) : (
        <Collapse
          activeKey={activeKeys}
          onChange={setActiveKeys}
          ghost
          style={{ marginBottom: 16 }}
          expandIcon={({ isActive }) => (
            // <DownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: 12 }} />

            isActive ? <iconify-icon icon="icon-park-outline:down" width="20" height="20"></iconify-icon> : <iconify-icon icon="icon-park-outline:right" width="20" height="20"></iconify-icon>

          )}
        >
          {emailConfig.emails.map(({ _id, email, name, cta_types, enable }) => {
            const counts = getNotificationCounts(cta_types);

            return (
              <Panel
                key={_id}
                style={{
                  border: "1px solid #e8e8e8",
                  borderRadius: 8,
                  marginBottom: 12,
                  overflow: "hidden",
                  backgroundColor: "#fafafa",
                }}
                header={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      width: "100%",
                      paddingRight: 16,
                      gap: 12,
                      flexDirection: isMobile ? "column" : "row",
                    }}
                    onClick={(e) => {
                      // Prevent collapse toggle when clicking on actions
                      if (e.target.closest('.email-actions')) {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <Space size={12} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                      {/* <MailOutlined style={{ fontSize: 16, color: enable ? "#1890ff" : "#bfbfbf" }} /> */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Text strong style={{ fontSize: 14 }}>
                            {name}
                          </Text>
                          <Badge size="small" count={enable ? "Active" : "Inactive"} style={{ backgroundColor: enable ? '#52c41a' : '#8c8c8c' }} />
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {email}
                        </Text>
                      </div>
                    </Space>

                    <Space
                      size={12}
                      align="center"
                      className="email-actions"
                      style={{
                        width: isMobile ? "100%" : "auto",
                        justifyContent: isMobile ? "space-between" : "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <Space size={8}>
                        {/* <Tooltip title={`${counts.total} total notification${counts.total !== 1 ? 's' : ''}`}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '4px 10px',
                              borderRadius: 6,
                              backgroundColor: '#e6f7ff',
                              border: '1px solid #91d5ff',
                              cursor: 'default',
                            }}
                          >
                            <Text 
                              style={{ 
                                color: '#1890ff',
                                fontSize: 13,
                                fontWeight: 500,
                                lineHeight: 1,
                              }}
                            >
                              Total: {counts.total}
                            </Text>
                          </div>
                        </Tooltip> */}
                        <Tooltip
                          title={`${counts.enabled} notification${counts.enabled !== 1 ? 's' : ''} enabled`}
                          trigger={["hover"]}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '4px 10px',
                              borderRadius: 6,
                              backgroundColor: '#f6ffed',
                              border: '1px solid #b7eb8f',
                              cursor: 'pointer',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpandRecipient(_id);
                            }}
                          >
                            <CheckCircleOutlined
                              style={{
                                color: '#52c41a',
                                fontSize: 14,
                              }}
                            />
                            <Text
                              style={{
                                color: '#52c41a',
                                fontSize: 13,
                                lineHeight: 1,
                              }}
                            >
                              {counts.enabled} Enabled
                            </Text>
                          </div>
                        </Tooltip>
                      </Space>

                      {!isMobile && (
                        <Divider type="vertical" style={{ height: 28, margin: 0 }} />
                      )}

                      <Tooltip
                        title={
                          enable
                            ? "All notifications enabled. Click to disable all."
                            : "All notifications disabled. Click to enable all."
                        }
                      >
                        <Switch
                          size="small"
                          checked={enable}
                          onChange={(checked, e) => {
                            e.stopPropagation();
                            handleToggleEmailStatus(_id);
                          }}
                          onClick={(checked, e) => e.stopPropagation()}
                          disabled={!emailServiceStatus}
                        />
                      </Tooltip>

                      <Tooltip title="Remove this recipient">
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            showDeleteModal(_id);
                          }}
                          style={{ marginLeft: 4 }}
                        />
                      </Tooltip>
                    </Space>
                  </div>
                }
              >
                <div style={{
                  padding: "12px 16px",
                  backgroundColor: "#fff",
                  borderRadius: "0 0 8px 8px",
                }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                    Configure notification types for this recipient
                  </Text>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(2, minmax(0, 1fr))",
                      gap: 8,
                    }}
                  >
                    {cta_types.map((cta) => (
                      <div
                        key={cta._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          // backgroundColor: cta.enable && enable ? "#f6ffed" : "#fff",
                          border: `1px solid ${cta.enable && enable ? "#e8e8e8" : "#e8e8e8"}`,
                          borderRadius: 6,
                          transition: "all 0.2s ease",
                          minHeight: 40,
                          cursor: !enable ? "not-allowed" : "pointer",
                        }}
                        onClick={() => {
                          if (enable) {
                            handleToggleCta(_id, cta.name);
                          }
                        }}
                      >
                        <Space size={8} align="center">
                          <Tooltip
                            title={
                              !enable
                                ? "Enable email status first to modify individual notifications"
                                : cta.enable
                                  ? `Disable ${formatCtaName(cta.name)} notifications`
                                  : `Enable ${formatCtaName(cta.name)} notifications`
                            }
                          >
                            <Switch
                              size="small"
                              checked={cta.enable}
                              onChange={(checked, e) => {
                                e.stopPropagation();
                                handleToggleCta(_id, cta.name);
                              }}
                              disabled={!enable || !emailServiceStatus}
                            />
                          </Tooltip>
                          {/* <BellOutlined 
                            style={{ 
                              fontSize: 14, 
                              color: cta.enable && enable ? "#52c41a" : "#bfbfbf",
                            }} 
                          /> */}
                          <Text
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: enable ? "#262626" : "#bfbfbf",
                            }}
                          >
                            {formatCtaName(cta.name)}
                          </Text>
                        </Space>
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>
            );
          })}
        </Collapse>
      )}

      {/* Delete Modal */}
      <Modal
        title="Remove Email"
        open={isModalVisible}
        onOk={handleDelete}
        onCancel={handleCancelDelete}
        okText="Yes, Remove"
        cancelText="Cancel"
      >
        {selectedEmailObj && (
          <>
            <Text>
              Are you sure you want to remove{" "}
              <Text strong>
                {selectedEmailObj.name} ({selectedEmailObj.email})
              </Text>{" "}
              from notification recipients?
            </Text>
            <br />
            <br />
          </>
        )}
        <Text type="secondary">
          This email will stop receiving any notifications. You can re-add it
          later if needed.
        </Text>
      </Modal>

      {/* Add Recipient Modal */}
      <Modal
        title="Add Recipient"
        open={isAddModalVisible}
        onOk={handleAddEmail}
        onCancel={() => setIsAddModalVisible(false)}
        okText="Add Recipient"
        cancelText="Cancel"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 12 }}>
          <div>
            <Text strong>Email Address</Text>
            <Input
              placeholder="e.g. name@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
          <div>
            <Text strong>Display Name (Optional)</Text>
            <Input
              placeholder="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
        </div>
      </Modal>

      {/* Upgrade Plan Modal */}
      <UpgradeDialog
        visible={isUpgradeModalVisible}
        onClose={() => setIsUpgradeModalVisible(false)}
        feature="Email recipients"
      />
    </Card>
  );
};

export default EmailSettings;
